import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import db, { collection, query, where } from '../firebase/firestore';
import { subscribeWithRetry } from './useResilientSnapshot';
import { useAcademyPortalData } from '../contexts/academyPortalData';

// A conversation is unread when its last message came from someone else
// and is newer than this viewer's own lastReadAt entry for it (or they
// have no lastReadAt entry at all yet).
function isUnread(conv, uid) {
  if (!conv.lastMessage || conv.lastMessage.senderId === uid) return false;
  const readAt = conv.lastReadAt?.[uid];
  if (!readAt) return true;
  return conv.lastMessage.createdAt?.toMillis() > readAt.toMillis();
}

// Every conversation (direct + group) the current user is a participant
// in, newest-message-first, each annotated with `unread`. Extracted out of
// Messages.jsx so Home's "recent messages" section and the shell's tab
// badge can share the exact same query rather than re-implementing it.
export function useConversations() {
  const shared = useAcademyPortalData();
  const own = useConversationsLive(!shared);
  return shared ? shared.conversations : own;
}

export function useConversationsLive(enabled) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled || !user) return;
    const q = query(collection(db, 'conversations'), where('participantIds', 'array-contains', user.uid));
    return subscribeWithRetry(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data(), unread: isUnread({ id: d.id, ...d.data() }, user.uid) }));
      rows.sort((a, b) => (b.lastMessage?.createdAt?.seconds || 0) - (a.lastMessage?.createdAt?.seconds || 0));
      setConversations(rows);
      setLoading(false);
    }, { onGiveUp: () => setLoading(false) });
  }, [enabled, user]);

  const unreadCount = conversations.filter((c) => c.unread).length;

  return { conversations, loading, unreadCount };
}
